import { ReactNode, useState } from 'react';

import { SORT_ORDER } from '@customTypes/Common';
import DateFilter from 'src/components/DateFilter';

import Button, { BUTTON_TYPE } from '@components/Button';
import SearchFilter from '@components/SearchFilter';
import SelectableTable from '@components/SelectableTable';
import Spinner from '@components/Spinner';
import { ColumnDefinitionType } from '@components/Table';

import styles from './style.module.scss';

export type ColumnSortFunction = (data: any[], key: string | number | symbol, order: SORT_ORDER) => any[];

interface EntityMainLayoutProps<T extends { id: number; name: string }> {
  entities?: T[];
  loadingEntities: boolean;
  error: boolean;
  errorComponent: ReactNode;
  selected: number[];
  searchProps: {
    query: string;
    setQuery: (query: string) => void;
  };
  dateFilterProps?: {
    date: string | null;
    setDate: (date: string | null) => void;
  };
  columns: ColumnDefinitionType<T, keyof T>[];
  title: string;
  check: (row: T) => void;
  uncheck: (id: number) => void;
  checkAll: (rows: T[]) => void;
  uncheckAll: () => void;
  newOrderRoute: string;
}

const EntityMainLayout = <T extends { id: number; name: string }>(props: EntityMainLayoutProps<T>) => {
  const {
    entities,
    loadingEntities,
    error,
    errorComponent,
    selected,
    searchProps,
    columns,
    dateFilterProps,
    newOrderRoute,
    title,
    check,
    uncheck,
    checkAll,
    uncheckAll,
  } = props;
  const [filtered, setFiltered] = useState<T[]>([]);
  const { query, setQuery } = searchProps;

  const searchHandler = (value: string) => {
    setQuery(value);
    if (!entities) return;
    if (value.length > 2) {
      const searchValue = value.toLowerCase();
      const newFiltered = entities.filter((e) => String(e.id).startsWith(searchValue) || e.name.toLowerCase().includes(searchValue));
      setFiltered(newFiltered);
    } else {
      setFiltered([...entities]);
    }
  };

  const entitiesToShow = !query ? entities : filtered;

  return (
    <>
      <header className={styles.header}>
        <h2>{title}</h2>
        {selected.length > 0 && (
          <div className={styles.selectedBox}>
            <div className={styles.info}>
              <span style={{ marginRight: 30 }}>
                {selected.length} {title} selected
              </span>
              <span onClick={() => uncheckAll()} className={styles.deselectAll}>
                Deselect all
              </span>
            </div>
            <Button className={styles.button} type={BUTTON_TYPE.PRIMARY} linkTo={newOrderRoute}>
              New order
            </Button>
          </div>
        )}
      </header>
      {error && errorComponent}
      {!error && (
        <>
          <div className={styles.search}>
            <SearchFilter searchHandler={searchHandler} value={query} onClearHandler={() => searchHandler('')} placeholder={'Search by id and name'} />
            {dateFilterProps && (
              <DateFilter
                maxDate={new Date()}
                selected={dateFilterProps.date ? new Date(dateFilterProps.date) : null}
                onChange={(date) => {
                  if (date) {
                    date.setHours(0, 0, 0, 0);
                  }
                  dateFilterProps.setDate(date?.toISOString() || null);
                }}
              />
            )}
            <span>
              {entities && `${!query ? entities.length : filtered.length} / `}
              {entities && !loadingEntities ? entities.length : <Spinner size={10} />}
            </span>
          </div>

          <SelectableTable<T>
            data={entitiesToShow || []}
            columns={columns}
            selected={selected}
            check={(id) => check(id)}
            uncheck={(id) => uncheck(id)}
            checkAll={(ids) => checkAll(ids)}
            uncheckAll={() => uncheckAll()}
            idGetterFn={(entity) => entity.id}
            noDataComponent={
              <div style={{ display: 'flex', placeContent: 'center', padding: '1rem', width: '100%' }}>
                {!entities && loadingEntities ? (
                  <Spinner size={40} style={{ placeSelf: 'center' }} />
                ) : (
                  <h3 style={{ padding: '1rem', textAlign: 'center' }}>No matching {title}</h3>
                )}
              </div>
            }
          />
        </>
      )}
    </>
  );
};

export default EntityMainLayout;
